import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { getDeviceId } from "../lib/device";
import { useSession, setStoredToken } from "../lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const fragment = window.location.hash || "";
    const m = fragment.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);

    (async () => {
      try {
        // Exchange the session_id for a token + httpOnly cookie.
        const { data } = await axios.post(
          `${API}/auth/session`,
          { session_id, device_id: getDeviceId() },
          { withCredentials: true },
        );
        // Bulletproof fallback: persist the token in localStorage and attach
        // it as a Bearer header on subsequent calls. This protects against
        // browsers/contexts where the SameSite=None httpOnly cookie is dropped
        // (Safari ITP, third-party-cookie blocking, custom domains, etc.).
        if (data?.session_token) {
          setStoredToken(data.session_token);
        }
        await refresh();
        // Wipe the OAuth fragment and go home
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (e) {
        console.error("[auth] session exchange failed", e?.response?.status, e?.response?.data, e?.message);
        const status = e?.response?.status;
        const msg = status === 401
          ? "La session Google a expiré. Réessayez la connexion."
          : status === 502
            ? "Le service d'authentification est indisponible. Réessayez dans un instant."
            : "Échec de la connexion. Réessayez ou contactez le support.";
        setError(msg);
      }
    })();
  }, [navigate, refresh]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-[var(--ink)]">
        <div className="card p-8 max-w-md text-center" data-testid="auth-error-card">
          <div className="mx-auto w-12 h-12 rounded-full bg-[var(--surface-2)] flex items-center justify-center mb-4">
            <AlertCircle size={22} className="text-[var(--ink-dim)]" />
          </div>
          <h1 className="font-display text-2xl tracking-tight mb-2">Connexion impossible</h1>
          <p className="text-[13.5px] text-[var(--ink-dim)] mb-6 leading-relaxed">{error}</p>
          <button
            onClick={() => { window.location.href = "/"; }}
            data-testid="auth-error-home-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[12.5px] font-semibold border border-[var(--border-bright)] bg-[var(--surface-2)] text-[var(--ink)] hover:bg-[var(--surface)] transition"
          >
            Retour à l&apos;accueil <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-[var(--ink-dim)]">
      <div className="flex items-center gap-3" data-testid="auth-loading">
        <Loader2 size={20} className="animate-spin text-[var(--accent)]" />
        <span className="text-sm font-mono">Authentification…</span>
      </div>
    </div>
  );
}
